import React, { useState, useRef, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';

import { FormattedMessageWrappedInSpan } from '../../misc';
import { formatSnWithMaybeUndefinedVal, isUserRedirectedFromStrava } from '../../../utils/common';
import ExtendedSearchCategorySearchTab from './ExtendedSearchCategorySearchTab';
import ExtendedSearchGpsSearchTab from './ExtendedSearchGpsSearchTab';
import ExtendedSearchDualSearchTab from './ExtendedSearchDualSearchTab';
import ExtendedSearchSmartSearchTab from './ExtendedSearchSmartSearchTab';
import StartNumberSearch from '../event/StartNumberSearch';
import ContentControlledCarousel from '../../misc/ContentControlledCarousel';
import SubsectionInFrame from '../../misc/SubsectionInFrame';
import EventSearchAltSearchResultsHint from './EventSearchAltSearchResultsHint';
import {
  MAIN_SEARCH,
  CATEGORY,
  GPS,
  DUAL,
  SMART_SEARCH,
  PRESALE,
  MAIN_SEARCH_COMBINED,
  STRAVA,
} from '../../../utils/variables';
import ExtendedSearchStravaTab from './ExtendedSearchStravaTab';

////////////////////////////////////////////
// TODO MISSING TEST (make sure main search is set as default)
////////////////////////////////////////////

const EventSearchOptions = ({
  eventSearchOptionsInfo,
  hasSmartSearchTab,
  activeSlideName,
  startNumber,
  searchStartnumber,
  codeSecondFactorForFaceEvent,
  setMainSearchFormRef,
  onInputChange,
  onSearchTrigger,
  onExtendedSearchHintClosed,
  secondFactor,
  showSearchInstructionsDialog,
  hasExtendedSearch,
  showHowToBuyHint,
  onShowHowToBuyHint,
  isAccessGrantedByValidCodeFaceEvent,
  hasUserAccessToMainSearch,
  setSearchType,
  goBackToMainSearchResults,
  triggerPointingAnimation,
  hasDualSearchTab,
  isCombinedSearchEvent,
  isAccessGrantedByValidCode,
  goBackToMainSearchSlide,
  stravaCode,
}) => {
  const [animate, setAnimate] = useState(false);
  const [startNumberForCombined, setStartNumberForCombined] = useState();
  const [showCombinedBibError, setShowCombinedBibError] = useState(false);
  const ALT_HINT = 'alternative_option_hint';
  const location = useLocation();
  const match = useRouteMatch();
  const history = useHistory();

  const eventSearchOptionsRef = useRef();

  const isStravaSearchAvailable = useFlag('strava');
  const updateContext = useUnleashContext();

  const search = location.search;
  const searchParams = new URLSearchParams(search);
  const code2fa = searchParams.get('code2fa');

  const isRedirectedFromStrava = isUserRedirectedFromStrava(search);

  // combined search
  useEffect(() => {
    // for combined search
    // because user is redirected to the new url, everything is remount
    // because of that, we should set all values related to combined search based on url
    // TODO move the logic to StartNumberSearch component while refactoring it
    const startNumberFormParams = formatSnWithMaybeUndefinedVal(match.params.startnumber);
    if (!startNumberFormParams) {
      return;
    }
    const extractedBib = startNumberFormParams.match(/^(.+?)combined/)?.[1]
    if (extractedBib) {
      setStartNumberForCombined(extractedBib)
    } else {
      setStartNumberForCombined(startNumberFormParams)
    }
  }, [])

  // Unleash context is updated with event ID
  useEffect(() => {
    updateContext({ userId: 'event-' + eventSearchOptionsInfo.sgId });
  }, [eventSearchOptionsInfo.sgId, updateContext]);

  const handleProceedWithCombined = (startNumber) => {
    if (!startNumber) {
      setShowCombinedBibError(true);
    } else {
      setSearchType(MAIN_SEARCH);
      setStartNumberForCombined(startNumber);
    }
  }

  useEffect(() => {
    if (setMainSearchFormRef) {
      setMainSearchFormRef(eventSearchOptionsRef);
    }
  }, [eventSearchOptionsRef, setMainSearchFormRef]);

  useEffect(() => {
    if (triggerPointingAnimation) {
      setAnimate(true);
      const timeout = setTimeout(() => setAnimate(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [triggerPointingAnimation])

  useEffect(() => {
    const isOnCategoryPage = match.url.includes('category')
    if (notOnMainSearchPage() && !isOnCategoryPage) {
      setSearchType(ALT_HINT);
    } else if (!isOnCategoryPage && hasUserAccessToMainSearch && !isCombinedSearchEvent) {
      setSearchType(MAIN_SEARCH);
    } else if (isCombinedSearchEvent && !formatSnWithMaybeUndefinedVal(match.params.startnumber) && !isOnCategoryPage) {
      setSearchType(MAIN_SEARCH_COMBINED)
    }
  }, [notOnMainSearchPage]);

  const notOnMainSearchPage = () => {
    return /dual|category|gps|smartsearch/.test(match.url);
  };

  const isDualActive = activeSlideName === DUAL;

  const handleNavigateTo = (url) => {
    let fullUrl = url;
    let codeParam = codeSecondFactorForFaceEvent || code2fa;
    if (codeParam) {
      if (!codeParam.startsWith('?code2fa=')) {
        codeParam = `?code2fa=${codeParam}`;
      }
      fullUrl = `${fullUrl}${codeParam}`;
    }
    if (match.path === '/:language/event/:id') {
      history.push(fullUrl);
    } else {
      history.replace(fullUrl);
    }
  };

  const renderSearchTitle = () => {
    if (activeSlideName === MAIN_SEARCH || activeSlideName === MAIN_SEARCH_COMBINED) {
      return (
        <FormattedMessageWrappedInSpan
          id="eventSearchOptions.mainSearchTitle"
          defaultMessage="Search and order your Foto-Flat"
          className="typography uppercase typography-title_small"
        />
      );
    } else {
      return (
        <FormattedMessageWrappedInSpan
          id="eventSearchOptions.altSearchTitle"
          defaultMessage="Add more to your Foto-Flat"
          className="typography uppercase typography-title_small"
        />
      );
    }
  };

  const backButton = (
    <button
      className="full-width secondary-new subsection-action-button btn-in-media-list"
      onClick={() => {
        if (notOnMainSearchPage()) {
          goBackToMainSearchResults();
        } else if (goBackToMainSearchSlide) {
          goBackToMainSearchSlide();
        } else setSearchType(MAIN_SEARCH);
      }}
    >
      <FormattedMessageWrappedInSpan
        id="eventSearchOptions.backButton"
        defaultMessage="Back to main search"
        className="typography bold-text typography-title_xsmall"
      />
    </button>
  );

  return (
    <div
      ref={eventSearchOptionsRef}
      className={classNames('subsection-container', {
        'point-to-frame': animate,
      })}
    >
      {!isDualActive && (
        <SubsectionInFrame title={renderSearchTitle()} noSidePadding color="main">
          <ContentControlledCarousel
            activeSlideName={activeSlideName}
            className="row center-xs"
          >
            {hasUserAccessToMainSearch && isCombinedSearchEvent && (
              <StartNumberSearch
                slideName={MAIN_SEARCH_COMBINED}
                info={eventSearchOptionsInfo}
                onInputChange={onInputChange}
                onSearchTrigger={onSearchTrigger}
                onExtendedSearchHintClosed={onExtendedSearchHintClosed}
                startNumber={startNumber}
                secondFactor={secondFactor}
                showSearchInstructionsDialog={showSearchInstructionsDialog}
                hasExtendedSearch={hasExtendedSearch}
                showHowToBuyHint={showHowToBuyHint}
                onShowHowToBuyHint={onShowHowToBuyHint}
                isAccessGrantedByValidCodeFaceEvent={isAccessGrantedByValidCodeFaceEvent}
                codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
                isCombinedSearchEvent={true}
                handleProceedWithCombined={handleProceedWithCombined}
                wasCombinedBibShown={false}
                showStartNumberErrorFromParent={showCombinedBibError}
                isRedirectedFromStrava={isRedirectedFromStrava}
              />
            )}
            {hasUserAccessToMainSearch && (
              <StartNumberSearch
                slideName={MAIN_SEARCH}
                info={eventSearchOptionsInfo}
                onInputChange={onInputChange}
                onSearchTrigger={onSearchTrigger}
                onExtendedSearchHintClosed={onExtendedSearchHintClosed}
                startNumber={startNumber}
                secondFactor={secondFactor}
                showSearchInstructionsDialog={showSearchInstructionsDialog}
                hasExtendedSearch={hasExtendedSearch}
                showHowToBuyHint={showHowToBuyHint}
                onShowHowToBuyHint={onShowHowToBuyHint}
                isAccessGrantedByValidCodeFaceEvent={isAccessGrantedByValidCodeFaceEvent}
                codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
                isCombinedSearchEvent={isCombinedSearchEvent}
                wasCombinedBibShown={true}
                startNumberForCombined={startNumberForCombined}
                isRedirectedFromStrava={isRedirectedFromStrava}
              />
            )}
            {hasSmartSearchTab && (
              <ExtendedSearchSmartSearchTab
                slideName={SMART_SEARCH}
                startNumber={formatSnWithMaybeUndefinedVal(match.params.startnumber)}
                eventId={eventSearchOptionsInfo.sgId}
                codeFactorFromUrl={code2fa}
              />
            )}
            {eventSearchOptionsInfo.searchCollections && (
              <ExtendedSearchCategorySearchTab
                slideName={CATEGORY}
                categories={eventSearchOptionsInfo}
                onNavigateTo={handleNavigateTo}
                eventId={eventSearchOptionsInfo.sgId}
                startNumber={formatSnWithMaybeUndefinedVal(startNumber)}
              />
            )}
            {isStravaSearchAvailable && (
              <ExtendedSearchStravaTab
                slideName={STRAVA}
                onNavigateTo={handleNavigateTo}
                eventId={eventSearchOptionsInfo.sgId}
                startNumber={formatSnWithMaybeUndefinedVal(match.params.startnumber)}
                stravaCode={stravaCode}
                eventDates={eventSearchOptionsInfo}
              />
            )}
            {eventSearchOptionsInfo.gps && (
              <ExtendedSearchGpsSearchTab
                slideName={GPS}
                onNavigateTo={handleNavigateTo}
                eventId={eventSearchOptionsInfo.sgId}
                startNumber={formatSnWithMaybeUndefinedVal(match.params.startnumber)}
                stravaCode={stravaCode}
                eventDates={eventSearchOptionsInfo}
              />
            )}
            <EventSearchAltSearchResultsHint
              slideName={ALT_HINT}
            />
          </ContentControlledCarousel>
          {![MAIN_SEARCH, PRESALE, MAIN_SEARCH_COMBINED].includes(activeSlideName) && backButton}
        </SubsectionInFrame>
      )}

      {/* TODO after refactoring StartNumberSearch move this to main carousel - this is temporary workaround, StartNumberSearch component twice in the carousel is breaking navigation completely, workaraound is saving functionality, but animation is broken */}
      {hasDualSearchTab && isDualActive && (
        <SubsectionInFrame title={renderSearchTitle()} noSidePadding color="main">
          <ExtendedSearchDualSearchTab
            slideName={DUAL}
            event={eventSearchOptionsInfo}
            startNumber={startNumber}
            searchStartnumber={searchStartnumber}
            isAccessGrantedByValidCode={isAccessGrantedByValidCode}
            isRedirectedFromStrava={isRedirectedFromStrava}
          />
          {backButton}
        </SubsectionInFrame>
      )}
    </div>
  );
};

EventSearchOptions.propTypes = {
  eventSearchOptionsInfo: PropTypes.object,
  hasSmartSearchTab: PropTypes.bool,
  activeSlideName: PropTypes.string,
  startNumber: PropTypes.string,
  searchStartnumber: PropTypes.string,
  codeSecondFactorForFaceEvent: PropTypes.string,
  setMainSearchFormRef: PropTypes.func,
  onInputChange: PropTypes.func,
  onSearchTrigger: PropTypes.func,
  onExtendedSearchHintClosed: PropTypes.func,
  secondFactor: PropTypes.string,
  showSearchInstructionsDialog: PropTypes.bool,
  hasExtendedSearch: PropTypes.bool,
  showHowToBuyHint: PropTypes.bool,
  onShowHowToBuyHint: PropTypes.func,
  isAccessGrantedByValidCodeFaceEvent: PropTypes.bool,
  hasUserAccessToMainSearch: PropTypes.bool,
  goBackToMainSearchSlide: PropTypes.func,
  setSearchType: PropTypes.func,
  goBackToMainSearchResults: PropTypes.func,
  triggerPointingAnimation: PropTypes.bool,
  hasDualSearchTab: PropTypes.bool,
  isCombinedSearchEvent: PropTypes.bool,
  isAccessGrantedByValidCode: PropTypes.bool,
  stravaCode: PropTypes.string,
};

export { EventSearchOptions };
export default createFragmentContainer(EventSearchOptions, {
  eventSearchOptionsInfo: graphql`
    fragment EventSearchOptions_eventSearchOptionsInfo on Event {
      id
      sgId
      mainSearch
      searchDual
      gps
      searchCollections
      ...ExtendedSearchCategorySearchTab_categories
      ...ExtendedSearchDualSearchTab_event
      ...StartNumberSearch_info
      ...ExtendedSearchGpsSearchTab_eventDates
      ...ExtendedSearchStravaTab_eventDates
    }
  `,
});
